import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import databaseAPI from '../api/databaseAPI';
import '../styles/Inventory.css';
import { authFetch, authHeaders } from '../api/auth';

function asInt(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function normalizeKey(k) {
  return String(k || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function parseMaybeExcelId(v) {
  const s = String(v ?? '').trim();
  if (!s) return '';
  const m = s.match(/^=\"(.+)\"$/);
  return m ? m[1] : s;
}

function csvSafeId(v) {
  const s = String(v ?? '').trim();
  return s ? `="${s}"` : '';
}

function excelSafeId(v) {
  return String(v ?? '').trim();
}

const INVENTORY_EXPORT_HEADERS = [
  'inventory_item_id',
  'product_id',
  'product_title',
  'variant_id',
  'sku',
  'location_id',
  'unavailable',
  'committed',
  'available',
  'on_hand',
  'reason',
  'quantity_change',
  'note',
];

const IMPORT_SAMPLE_HEADERS = [
  'inventory_item_id',
  'available',
  'reason',
];

function buildImportSampleRows(rows) {
  const sampleBase = rows[0] || {};
  const itemId = sampleBase.inventoryItemId ? String(sampleBase.inventoryItemId) : '';
  return [
    {
      inventory_item_id: itemId,
      available: 20,
      reason: 'manual_correction',
    },
    {
      inventory_item_id: itemId,
      available: 50,
      reason: 'qc_fail',
    },
  ];
}

function sheetFromRows(rows, headers) {
  return XLSX.utils.json_to_sheet(rows, { header: headers, skipHeader: false });
}

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLabel, setLoadingLabel] = useState('Loading…');
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [pendingImportRows, setPendingImportRows] = useState([]);
  const [pendingImportFileName, setPendingImportFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [draftByVariantId, setDraftByVariantId] = useState({});
  const [activeTab, setActiveTab] = useState('inventory');
  const [adjustingId, setAdjustingId] = useState(null);
  const [deltaByVariantId, setDeltaByVariantId] = useState({});
  const [reasonByVariantId, setReasonByVariantId] = useState({});
  const [noteByVariantId, setNoteByVariantId] = useState({});
  const [reasons, setReasons] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [nextPageInfo, setNextPageInfo] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [levelsByItemId, setLevelsByItemId] = useState({});
  const levelsByItemIdRef = useRef({});
  const nextPageInfoRef = useRef(null);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const locationIdRef = useRef('');
  const productsRef = useRef([]);
  const importInputRef = useRef(null);
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    nextPageInfoRef.current = nextPageInfo;
  }, [nextPageInfo]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    locationIdRef.current = locationId;
  }, [locationId]);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    levelsByItemIdRef.current = levelsByItemId || {};
  }, [levelsByItemId]);

  useEffect(() => {
    let cancelled = false;
    const pageSize = 250;

    const fetchPage = async ({ pageInfo = null, reset = false } = {}) => {
      const base = databaseAPI.baseURL || '';
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      if (pageInfo) params.set('page_info', pageInfo);
      const res = await authFetch(`${base}/products/?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const items = Array.isArray(data.products) ? data.products : [];
      const next = data.next_page_info || null;
      return { items, next };
    };

    const initialLoad = async () => {
      setLoading(true);
      setLoadingLabel('Loading products…');
      setError('');
      try {
        const { items, next } = await fetchPage({ pageInfo: null, reset: true });
        const sorted = items.slice().sort((a, b) => {
          const ta = new Date(a.shopify_created_at || a.created_at || 0).getTime();
          const tb = new Date(b.shopify_created_at || b.created_at || 0).getTime();
          return tb - ta;
        });

        if (!cancelled) {
          setProducts(sorted);
          setNextPageInfo(next);
          setHasMore(Boolean(next));
          setLoadingLabel(`Loaded ${sorted.length} products`);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const loadMorePage = async () => {
      const nextInfo = nextPageInfoRef.current;
      if (!nextInfo) return;
      if (cancelled) return;
      if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
      setLoadingMore(true);
      try {
        const { items, next } = await fetchPage({ pageInfo: nextInfo, reset: false });
        if (cancelled) return;
        setProducts((prev) => {
          const seen = new Set(prev.map((p) => String(p.id ?? p.shopify_id)));
          const merged = [...prev];
          for (const p of items) {
            const id = String(p.id ?? p.shopify_id);
            if (!id || seen.has(id)) continue;
            seen.add(id);
            merged.push(p);
          }
          merged.sort((a, b) => {
            const ta = new Date(a.shopify_created_at || a.created_at || 0).getTime();
            const tb = new Date(b.shopify_created_at || b.created_at || 0).getTime();
            return tb - ta;
          });
          return merged;
        });
        setNextPageInfo(next);
        setHasMore(Boolean(next) && (items || []).length > 0);
      } catch (e) {
        setError(e.message || 'Failed to load more products');
        setHasMore(false);
      } finally {
        if (!cancelled) setLoadingMore(false);
      }
    };

    const onScroll = async () => {
      if (cancelled) return;
      const threshold = 800;
      const scrollPos = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
      if (pageHeight - scrollPos > threshold) return;
      await loadMorePage();
    };

    initialLoad();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const refreshLevelsForProducts = async (prods, locId, { force = false } = {}) => {
    const ids = [];
    for (const p of prods || []) {
      for (const v of p.variants || []) {
        if (v && v.inventory_item_id) ids.push(v.inventory_item_id);
      }
    }
    const uniq = Array.from(new Set(ids.map(String)))
      .filter((s) => force || !(s in (levelsByItemIdRef.current || {})))
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));
    if (uniq.length === 0) return;

    const res = await databaseAPI.getInventoryLevels({
      inventoryItemIds: uniq,
      locationId: locId || undefined,
    });
    if (!res.success) return;
    setLevelsByItemId((prev) => {
      const next = { ...(prev || {}) };
      for (const l of res.levels || []) {
        if (l?.inventory_item_id == null) continue;
        next[String(l.inventory_item_id)] = l;
      }
      return next;
    });
  };

  // Fetch inventory levels for current products when location changes or products list grows.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loc = asInt(locationId);
      if (locationId) {
        // Location changed: refresh levels from scratch (levels are location-specific).
        levelsByItemIdRef.current = {};
        setLevelsByItemId({});
      }
      const snapshot = productsRef.current;
      await refreshLevelsForProducts(snapshot, loc, { force: Boolean(locationId) });
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId, products]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await databaseAPI.getLocations();
      if (!cancelled && res.success) {
        setLocations(res.locations || []);
        const active = (res.locations || []).find((l) => l.active);
        if (active) setLocationId(String(active.id));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [rs, hs] = await Promise.all([
        databaseAPI.getInventoryReasons(),
        databaseAPI.getInventoryAdjustments({ limit: 50 }),
      ]);
      if (!cancelled && rs.success) setReasons(rs.reasons || []);
      if (!cancelled && hs.success) setAdjustments(hs.adjustments || []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = [];
    for (const p of products || []) {
      const title = p.title || '';
      if (q && !title.toLowerCase().includes(q) && !(p.vendor || '').toLowerCase().includes(q)) continue;
      const variants = Array.isArray(p.variants) ? p.variants : [];
      for (const v of variants) {
        const invItemId = v.inventory_item_id;
        const lvl = invItemId ? levelsByItemId[String(invItemId)] : null;
        const available = lvl?.available ?? v.inventory_quantity;
        const committed = lvl?.committed ?? 0;
        const unavailable = lvl?.unavailable ?? 0;
        const onHand = lvl?.on_hand ?? available;
        out.push({
          productId: p.id,
          productTitle: title,
          variantId: v.id,
          variantTitle: v.title || '',
          sku: v.sku || '',
          inventoryItemId: invItemId,
          tracked: v.inventory_management ? true : false,
          unavailable,
          committed,
          available,
          onHand,
        });
      }
    }
    return out;
  }, [products, query, levelsByItemId]);

  const rowByInventoryItemId = useMemo(() => {
    const map = {};
    for (const r of rows) {
      if (r.inventoryItemId != null) map[String(r.inventoryItemId)] = r;
    }
    return map;
  }, [rows]);

  const setDraft = (variantId, value) => {
    setDraftByVariantId((prev) => ({ ...(prev || {}), [String(variantId)]: value }));
  };

  const setDelta = (variantId, value) => {
    setDeltaByVariantId((prev) => ({ ...(prev || {}), [String(variantId)]: value }));
  };

  const setReason = (variantId, value) => {
    setReasonByVariantId((prev) => ({ ...(prev || {}), [String(variantId)]: value }));
  };

  const setNote = (variantId, value) => {
    setNoteByVariantId((prev) => ({ ...(prev || {}), [String(variantId)]: value }));
  };

  const handleSave = async (row) => {
    const invId = asInt(row.inventoryItemId);
    const draft = draftByVariantId[String(row.variantId)];
    const avail = asInt(draft != null ? draft : row.available);
    const loc = asInt(locationId);
    if (!invId || avail == null) {
      setError('Missing inventory_item_id or available quantity');
      return;
    }
    setSavingId(row.variantId);
    setError('');
    const res = await databaseAPI.setInventoryLevel({
      inventoryItemId: invId,
      available: avail,
      locationId: loc || undefined,
    });
    if (!res.success) {
      setError(res.error || 'Inventory update failed');
      setSavingId(null);
      return;
    }
    // Optimistically update shown levels so input doesn't bounce back.
    setLevelsByItemId((prev) => ({
      ...(prev || {}),
      [String(invId)]: {
        ...(prev || {})[String(invId)],
        inventory_item_id: invId,
        location_id: loc || (prev || {})[String(invId)]?.location_id,
        available: avail,
      },
    }));
    setDraftByVariantId((prev) => {
      const next = { ...(prev || {}) };
      delete next[String(row.variantId)];
      return next;
    });
    setSavingId(null);
  };

  const handleAdjust = async (row) => {
    const key = String(row.variantId);
    const invId = asInt(row.inventoryItemId);
    const delta = asInt(deltaByVariantId[key]);
    const reason = reasonByVariantId[key] || 'manual_correction';
    const note = noteByVariantId[key] || '';
    const loc = asInt(locationId);
    if (!invId || delta == null || delta === 0) {
      setError('Adjustment delta required (non-zero).');
      return;
    }
    setAdjustingId(row.variantId);
    setError('');
    const res = await databaseAPI.createInventoryAdjustment({
      inventory_item_id: invId,
      product_id: row.productId,
      variant_id: row.variantId,
      location_id: loc || undefined,
      quantity_change: delta,
      reason,
      note,
      created_by: 'admin',
    });
    if (!res.success) {
      setError(res.error || 'Adjustment failed');
      setAdjustingId(null);
      return;
    }
    setLevelsByItemId((prev) => ({
      ...(prev || {}),
      [String(invId)]: {
        ...(prev || {})[String(invId)],
        inventory_item_id: invId,
        location_id: loc || (prev || {})[String(invId)]?.location_id,
        available: res.adjustment?.new_available ?? ((prev || {})[String(invId)]?.available ?? 0) + delta,
        on_hand: res.inventory_level?.available ?? res.adjustment?.new_available,
      },
    }));
    setAdjustments((prev) => [res.adjustment, ...(prev || [])].slice(0, 100));
    setDeltaByVariantId((prev) => ({ ...(prev || {}), [key]: '' }));
    setNoteByVariantId((prev) => ({ ...(prev || {}), [key]: '' }));
    setAdjustingId(null);
  };

  const exportInventoryRows = () => rows.map((r) => ({
    inventory_item_id: r.inventoryItemId,
    product_id: r.productId,
    product_title: r.productTitle,
    variant_id: r.variantId,
    sku: r.sku || '',
    location_id: asInt(locationId) || '',
    unavailable: r.unavailable ?? 0,
    committed: r.committed ?? 0,
    available: r.available ?? 0,
    on_hand: r.onHand ?? 0,
    reason: reasonByVariantId[String(r.variantId)] || 'manual_correction',
    quantity_change: '',
    note: '',
  }));

  const exportInventoryRowsForExcel = () => exportInventoryRows().map((r) => ({
    ...r,
    inventory_item_id: excelSafeId(r.inventory_item_id),
    product_id: excelSafeId(r.product_id),
    variant_id: excelSafeId(r.variant_id),
    location_id: excelSafeId(r.location_id),
  }));

  const exportAdjustmentsRows = () => (adjustments || []).map((a) => ({
    created_at: a.created_at || '',
    inventory_item_id: a.inventory_item_id,
    location_id: a.location_id,
    reason: a.reason,
    quantity_change: a.quantity_change,
    previous_available: a.previous_available,
    new_available: a.new_available,
    note: a.note || '',
    product_id: a.product_id || '',
    variant_id: a.variant_id || '',
  }));

  const exportInventoryRowsCsvSafe = () => exportInventoryRows().map((r) => ({
    ...r,
    product_id: csvSafeId(r.product_id),
    variant_id: csvSafeId(r.variant_id),
    inventory_item_id: csvSafeId(r.inventory_item_id),
    location_id: csvSafeId(r.location_id),
  }));

  const exportAdjustmentsRowsCsvSafe = () => exportAdjustmentsRows().map((r) => ({
    ...r,
    product_id: csvSafeId(r.product_id),
    variant_id: csvSafeId(r.variant_id),
    inventory_item_id: csvSafeId(r.inventory_item_id),
    location_id: csvSafeId(r.location_id),
  }));

  const downloadWorkbook = (wb, filename) => {
    XLSX.writeFile(wb, filename);
  };

  const handleExportCsv = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromRows(exportInventoryRowsCsvSafe(), INVENTORY_EXPORT_HEADERS),
      'inventory_import_template',
    );
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromRows(exportAdjustmentsRowsCsvSafe(), [
        'created_at',
        'inventory_item_id',
        'location_id',
        'reason',
        'quantity_change',
        'previous_available',
        'new_available',
        'note',
        'product_id',
        'variant_id',
      ]),
      'recent_adjustments',
    );
    downloadWorkbook(wb, `inventory_export_${Date.now()}.csv`);
  };

  const handleExportAdjustmentsCsv = () => {
    const sheet = XLSX.utils.json_to_sheet(exportAdjustmentsRowsCsvSafe());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'recent_adjustments');
    downloadWorkbook(wb, `inventory_adjustments_export_${Date.now()}.csv`);
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromRows(exportInventoryRowsForExcel(), INVENTORY_EXPORT_HEADERS),
      'inventory_import_template',
    );
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromRows(
        exportAdjustmentsRows().map((r) => ({
          ...r,
          product_id: excelSafeId(r.product_id),
          variant_id: excelSafeId(r.variant_id),
          inventory_item_id: excelSafeId(r.inventory_item_id),
          location_id: excelSafeId(r.location_id),
        })),
        [
          'created_at',
          'inventory_item_id',
          'location_id',
          'reason',
          'quantity_change',
          'previous_available',
          'new_available',
          'note',
          'product_id',
          'variant_id',
        ],
      ),
      'recent_adjustments',
    );
    downloadWorkbook(wb, `inventory_export_${Date.now()}.xlsx`);
  };

  const handleDownloadImportSample = (format = 'xlsx') => {
    const sample = buildImportSampleRows(rows).map((row) => ({
      inventory_item_id: format === 'csv' ? csvSafeId(row.inventory_item_id) : excelSafeId(row.inventory_item_id),
      available: row.available,
      reason: row.reason,
    }));
    const sheet = sheetFromRows(sample, IMPORT_SAMPLE_HEADERS);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'sample_import');
    const ext = format === 'csv' ? 'csv' : 'xlsx';
    downloadWorkbook(wb, `inventory_import_sample_${Date.now()}.${ext}`);
  };

  const parseImportRows = (rawRows) => {
    const parsed = [];
    for (const raw of rawRows || []) {
      const row = {};
      for (const [k, v] of Object.entries(raw || {})) {
        row[normalizeKey(k)] = v;
      }
      const inventoryItemId = parseMaybeExcelId(row.inventory_item_id ?? row.inventoryitemid ?? row.item_id);
      const reason = row.reason || 'manual_correction';
      if (!inventoryItemId) continue;

      let quantityChange = row.quantity_change ?? row.delta ?? row.qty_change;
      let targetAvailable = null;

      if (quantityChange == null || quantityChange === '') {
        targetAvailable = asInt(
          row.available ?? row.on_hand ?? row.target ?? row.target_available ?? row.new_available,
        );
        if (targetAvailable == null) {
          const plus = asInt(row.plus ?? row.increase ?? row.qty_plus) || 0;
          const minus = asInt(row.minus ?? row.decrease ?? row.qty_minus) || 0;
          quantityChange = plus - minus;
        }
      }

      const item = {
        inventory_item_id: asInt(inventoryItemId),
        product_id: asInt(parseMaybeExcelId(row.product_id)),
        variant_id: asInt(parseMaybeExcelId(row.variant_id)),
        location_id: asInt(parseMaybeExcelId(row.location_id)) || asInt(locationId) || undefined,
        reason,
        note: row.note ? String(row.note) : '',
        created_by: 'admin',
      };

      if (targetAvailable != null) {
        const current = levelsByItemIdRef.current?.[String(item.inventory_item_id)]?.available;
        item.target_available = targetAvailable;
        if (current != null) {
          const delta = targetAvailable - Number(current);
          if (delta === 0) continue;
          item.quantity_change = delta;
        }
        parsed.push(item);
        continue;
      }

      const q = asInt(quantityChange);
      if (q == null || q === 0) continue;
      item.quantity_change = q;
      parsed.push(item);
    }
    return parsed;
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setError('');
    setImportMessage('');
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const firstSheet = wb.SheetNames[0];
      if (!firstSheet) {
        setError('Import file empty.');
        return;
      }
      const ws = wb.Sheets[firstSheet];
      const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const rowsToImport = parseImportRows(rawRows);
      if (rowsToImport.length === 0) {
        setError('No valid rows found. Required: inventory_item_id + available (target stock) or quantity_change.');
        return;
      }
      setPendingImportRows(rowsToImport);
      setPendingImportFileName(file.name || 'selected file');
      setImportMessage(`Ready to import ${rowsToImport.length} rows. Click "Import Now" to continue.`);
    } catch (e) {
      setError(e.message || 'Import parse failed');
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingImportRows.length) {
      setError('No parsed rows to import.');
      return;
    }
    setImporting(true);
    setError('');
    setImportMessage('');
    try {
      const res = await databaseAPI.createInventoryAdjustmentsBulk({
        rows: pendingImportRows,
        locationId: asInt(locationId) || undefined,
      });
      if (!res.success) {
        setError(res.error || 'Bulk import failed');
        setImporting(false);
        return;
      }
      const okRows = (res.results || []).filter((x) => x.success && x.adjustment).map((x) => x.adjustment).filter(Boolean);
      const skippedCount = (res.results || []).filter((x) => x.success && x.skipped).length;
      const failCount = Number(res.failed_count || 0);
      if (okRows.length) setAdjustments((prev) => [...okRows, ...(prev || [])].slice(0, 100));
      for (const a of okRows) {
        setLevelsByItemId((prev) => ({
          ...(prev || {}),
          [String(a.inventory_item_id)]: {
            ...(prev || {})[String(a.inventory_item_id)],
            inventory_item_id: a.inventory_item_id,
            location_id: a.location_id,
            available: a.new_available,
          },
        }));
      }
      setPendingImportRows([]);
      setPendingImportFileName('');
      setImportMessage(
        `Import complete: ${okRows.length} updated${skippedCount ? `, ${skippedCount} already at target` : ''}${failCount ? `, ${failCount} failed` : ''}.`,
      );
      if (failCount > 0) setError(`Some rows failed (${failCount}). Please review data and retry.`);
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const formatIstDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  };

  return (
    <div className="content-container inventory-page">
      <div className="inventory-hero">
        <h1>Inventory</h1>
        <p>Update variant stock here. This sets Shopify inventory level for the selected location.</p>
      </div>

      <div className="inventory-toolbar card">
        <div className="inventory-toolbar-grid">
          <label className="inv-label">
            Search
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search product title / vendor"
            />
          </label>
          <label className="inv-label">
            Location
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Default (first active)</option>
              {locations.map((l) => (
                <option key={l.id} value={String(l.id)}>
                  {l.name} {l.active ? '' : '(inactive)'}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="inv-actions-row" ref={actionsMenuRef}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setActionsOpen((v) => !v)}
            aria-expanded={actionsOpen}
            aria-haspopup="menu"
          >
            Download
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => importInputRef.current?.click()}
          >
            Upload File
          </button>
          {actionsOpen ? (
            <div className="inv-actions-menu" role="menu">
              <button type="button" className="inv-actions-item" onClick={() => { handleDownloadImportSample('csv'); setActionsOpen(false); }}>
                Download Import Sample (CSV)
              </button>
              <button type="button" className="inv-actions-item" onClick={() => { handleDownloadImportSample('xlsx'); setActionsOpen(false); }}>
                Download Import Sample (Excel)
              </button>
              <button type="button" className="inv-actions-item" onClick={() => { handleExportCsv(); setActionsOpen(false); }}>
                Export CSV
              </button>
              <button type="button" className="inv-actions-item" onClick={() => { handleExportAdjustmentsCsv(); setActionsOpen(false); }}>
                Export Adjustments CSV
              </button>
              <button type="button" className="inv-actions-item" onClick={() => { handleExportExcel(); setActionsOpen(false); }}>
                Export Excel
              </button>
            </div>
          ) : null}
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => handleImportFile(e.target.files?.[0])}
          />
        </div>
        {pendingImportRows.length > 0 ? (
          <div className="inv-import-preview">
            <div className="inv-import-preview-title">
              File: <strong>{pendingImportFileName}</strong> | Parsed rows: <strong>{pendingImportRows.length}</strong>
              <span className="inv-import-hint"> — set the <strong>available</strong> column to your target stock; +/- is calculated automatically.</span>
            </div>
            <div className="table-wrap">
              <table className="inventory-table inv-import-preview-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Inventory item</th>
                    <th>Reason</th>
                    <th className="num">Target</th>
                    <th className="num">Change</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingImportRows.slice(0, 20).map((r, i) => {
                    const m = rowByInventoryItemId[String(r.inventory_item_id)] || {};
                    return (
                      <tr key={`${r.inventory_item_id}-${i}`}>
                        <td>{m.productTitle || `Product ID ${r.product_id || '—'}`}</td>
                        <td>{m.sku || '—'}</td>
                        <td>{r.inventory_item_id}</td>
                        <td>{r.reason}</td>
                        <td className="num">{r.target_available ?? '—'}</td>
                        <td className="num">
                          {r.quantity_change != null
                            ? (r.quantity_change > 0 ? `+${r.quantity_change}` : r.quantity_change)
                            : 'auto'}
                        </td>
                        <td>{r.note || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pendingImportRows.length > 20 ? (
              <div className="inv-import-preview-note">Showing first 20 rows preview.</div>
            ) : null}
            <div className="inv-import-preview-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={importing}
                onClick={handleConfirmImport}
              >
                {importing ? 'Importing…' : 'Import Now'}
              </button>
              <button
                type="button"
                className="btn"
                disabled={importing}
                onClick={() => {
                  setPendingImportRows([]);
                  setPendingImportFileName('');
                  setImportMessage('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
        {importMessage ? <div className="inv-import-message">{importMessage}</div> : null}
        {error ? <div className="inventory-error">{error}</div> : null}
      </div>

      <div className="card inventory-table-card">
        <div className="inv-tabs" role="tablist" aria-label="Inventory tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'inventory'}
            className={`inv-tab-btn${activeTab === 'inventory' ? ' active' : ''}`}
            onClick={() => setActiveTab('inventory')}
          >
            Inventory
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'adjustments'}
            className={`inv-tab-btn${activeTab === 'adjustments' ? ' active' : ''}`}
            onClick={() => setActiveTab('adjustments')}
          >
            Recent inventory adjustments
          </button>
        </div>
        {activeTab === 'inventory' ? (
          <div className="table-wrap">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th className="num">Unavailable</th>
                  <th className="num">Committed</th>
                  <th className="num">Available</th>
                  <th className="num">On hand</th>
                  <th>Reason</th>
                  <th>Note</th>
                  <th className="num">Delta</th>
                  <th className="num">Update</th>
                  <th className="num">Adjust</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11} className="muted">
                      {loadingLabel}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="muted">
                      No variants found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.variantId}>
                      <td className="inv-product">{r.productTitle}</td>
                      <td className="inv-sku">{r.sku || '—'}</td>
                      <td className="num">{r.unavailable ?? 0}</td>
                      <td className="num">{r.committed ?? 0}</td>
                      <td className="num">
                        <input
                          className="inv-qty"
                          type="number"
                          value={
                            draftByVariantId[String(r.variantId)] != null
                              ? draftByVariantId[String(r.variantId)]
                              : (r.available ?? 0)
                          }
                          onChange={(e) => setDraft(r.variantId, e.target.value)}
                          disabled={!r.tracked}
                          title={!r.tracked ? 'Inventory not tracked for this variant' : ''}
                        />
                      </td>
                      <td className="num">{r.onHand ?? r.available ?? 0}</td>
                      <td>
                        <select
                          className="inv-inline-select"
                          value={reasonByVariantId[String(r.variantId)] || 'manual_correction'}
                          onChange={(e) => setReason(r.variantId, e.target.value)}
                          disabled={!r.tracked}
                        >
                          {(reasons.length ? reasons : [{ value: 'manual_correction', label: 'Manual Correction' }]).map((x) => (
                            <option key={x.value} value={x.value}>{x.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="inv-inline-note"
                          value={noteByVariantId[String(r.variantId)] || ''}
                          onChange={(e) => setNote(r.variantId, e.target.value)}
                          disabled={!r.tracked}
                          placeholder="Optional note"
                        />
                      </td>
                      <td className="num">
                        <input
                          className="inv-delta"
                          type="number"
                          value={deltaByVariantId[String(r.variantId)] ?? ''}
                          onChange={(e) => setDelta(r.variantId, e.target.value)}
                          disabled={!r.tracked}
                          placeholder="-2 / +5"
                        />
                      </td>
                      <td className="num">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleSave(r)}
                          disabled={savingId === r.variantId || !r.tracked}
                          title={!r.tracked ? 'Inventory not tracked for this variant' : ''}
                        >
                          {savingId === r.variantId ? 'Saving…' : 'Save'}
                        </button>
                      </td>
                      <td className="num">
                        <button
                          className="btn"
                          onClick={() => handleAdjust(r)}
                          disabled={adjustingId === r.variantId || !r.tracked}
                          title={!r.tracked ? 'Inventory not tracked for this variant' : ''}
                        >
                          {adjustingId === r.variantId ? 'Applying…' : 'Adjust'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
                {!loading && (loadingMore || hasMore) ? (
                  <tr>
                    <td colSpan={11} className="muted inv-more">
                      {loadingMore ? 'Loading more…' : 'Scroll to load more…'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Item</th>
                  <th>Reason</th>
                  <th className="num">Delta</th>
                  <th className="num">Prev</th>
                  <th className="num">New</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.length === 0 ? (
                  <tr><td colSpan={7} className="muted">No adjustment history yet.</td></tr>
                ) : (
                  adjustments.slice(0, 30).map((a) => (
                    <tr key={a.id}>
                      <td>{formatIstDateTime(a.created_at)}</td>
                      <td>{a.inventory_item_id}</td>
                      <td>{a.reason}</td>
                      <td className="num">{a.quantity_change}</td>
                      <td className="num">{a.previous_available ?? '—'}</td>
                      <td className="num">{a.new_available ?? '—'}</td>
                      <td>{a.note || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;

